import type { FileMeta, TransferMessage, UserSettings } from '@flowshare/protocol';
import {
  MAX_FILE_BYTES,
  MAX_FILES_PER_SELECTION,
  TRANSFER_BUFFER_HIGH_WATER,
  TRANSFER_BUFFER_LOW_WATER,
  TRANSFER_CHUNK_SIZE,
  TRANSFER_MAX_CHUNK_SIZE,
  TRANSFER_MIN_CHUNK_SIZE,
  fileMetaSchema,
  serializeTransferMessage,
} from '@flowshare/protocol';

/** Minimal surface the transfer engine needs from a peer connection. */
export interface TransferTransport {
  readonly peerId: string;
  readonly peerName: string;
  isOpen: () => boolean;
  sendControl: (message: TransferMessage) => boolean;
  sendChunk: (frame: ArrayBuffer) => boolean;
  /** Completion must travel on the ordered data channel, after every chunk. */
  sendDataMessage: (message: TransferMessage) => boolean;
  waitForDrain: () => Promise<void>;
  bufferedAmount: () => number;
}

export class DataChannelTransport implements TransferTransport {
  constructor(
    readonly peerId: string,
    readonly peerName: string,
    private readonly control: RTCDataChannel,
    private readonly data: RTCDataChannel,
  ) {
    this.data.bufferedAmountLowThreshold = TRANSFER_BUFFER_LOW_WATER;
  }

  isOpen(): boolean {
    return this.control.readyState === 'open' && this.data.readyState === 'open';
  }

  sendControl(message: TransferMessage): boolean {
    if (this.control.readyState !== 'open') return false;
    try {
      this.control.send(serializeTransferMessage(message));
      return true;
    } catch {
      return false;
    }
  }

  sendChunk(frame: ArrayBuffer): boolean {
    if (this.data.readyState !== 'open') return false;
    try {
      this.data.send(frame);
      return true;
    } catch {
      return false;
    }
  }

  sendDataMessage(message: TransferMessage): boolean {
    if (this.data.readyState !== 'open') return false;
    try {
      this.data.send(serializeTransferMessage(message));
      return true;
    } catch {
      return false;
    }
  }

  waitForDrain(): Promise<void> {
    if (this.data.readyState !== 'open') return Promise.reject(new Error('CHANNEL_CLOSED'));
    // Let the channel buffer fill up to the high-water mark before pausing.
    // Waiting at the low-water mark starves the SCTP congestion window.
    if (this.data.bufferedAmount < TRANSFER_BUFFER_HIGH_WATER) return Promise.resolve();

    const channel = this.data;
    return new Promise<void>((resolve, reject) => {
      const cleanup = () => {
        channel.removeEventListener('bufferedamountlow', onLow);
        channel.removeEventListener('close', onClose);
      };
      const onLow = () => {
        cleanup();
        resolve();
      };
      const onClose = () => {
        cleanup();
        reject(new Error('CHANNEL_CLOSED'));
      };

      channel.bufferedAmountLowThreshold = TRANSFER_BUFFER_LOW_WATER;
      channel.addEventListener('bufferedamountlow', onLow);
      channel.addEventListener('close', onClose);
    });
  }

  bufferedAmount(): number {
    return this.data.bufferedAmount;
  }
}

export function clampChunkSize(value: number): number {
  if (!Number.isFinite(value)) return TRANSFER_CHUNK_SIZE;
  return Math.min(TRANSFER_MAX_CHUNK_SIZE, Math.max(TRANSFER_MIN_CHUNK_SIZE, Math.floor(value)));
}

export function totalChunksFor(size: number, chunkSize: number): number {
  if (size <= 0) return 0;
  return Math.ceil(size / chunkSize);
}

export function fileMetaFromFile(file: File): FileMeta {
  const path =
    (file as File & { relativePath?: string }).relativePath ??
    (file as File & { webkitRelativePath?: string }).webkitRelativePath;
  return {
    name: file.name,
    size: file.size,
    mimeType: file.type || null,
    lastModified: file.lastModified,
    ...(path && path.length > 0 ? { relativePath: path } : {}),
  };
}

export function validateSelection(files: File[]): string | null {
  if (files.length === 0) return 'Choose at least one file.';
  if (files.length > MAX_FILES_PER_SELECTION) {
    return `You can send up to ${MAX_FILES_PER_SELECTION} files at a time.`;
  }
  for (const file of files) {
    if (file.size > MAX_FILE_BYTES) return `${file.name} is larger than the 64 GB limit.`;
    if (!fileMetaSchema.safeParse(fileMetaFromFile(file)).success) {
      return `${file.name} has a name or path this browser cannot send.`;
    }
  }
  return null;
}

export type TransferSettings = Pick<
  UserSettings,
  'autoAcceptTransfers' | 'autoDownload' | 'maxConcurrentTransfers'
>;
